import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { addIcons } from 'ionicons';

import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';

import { Storage } from '@ionic/storage-angular';

import { FormsModule } from '@angular/forms';
import {
  AlertController,
  IonApp,
  IonIcon,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  MenuController,
  Platform,
  ToastController,
} from '@ionic/angular/standalone';
import {
  businessOutline,
  calendarOutline,
  chevronBackOutline,
  chevronForwardOutline,
  folderOutline,
  gridOutline,
  informationCircleOutline,
  logInOutline,
  logOutOutline,
  menuOutline,
  moonOutline,
  personAddOutline,
  personOutline,
  phonePortraitOutline,
  swapHorizontalOutline,
  bluetoothOutline,
  flaskOutline,
  cubeOutline,
  repeatOutline,
  notificationsOutline,
  peopleOutline,
  checkmarkCircleOutline,
  linkOutline,
} from 'ionicons/icons';
import { UserService } from './providers/user.service';
import { AppPagesBase } from './app.pages.base';
import { JwtService } from './providers/jwt.service';
import { CommonModule } from '@angular/common';
import { PushNotificationService } from './providers';
import { TranslateService } from '@ngx-translate/core';
import { menuItemPage } from './pages/menu-item/menu-item.page';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    FormsModule,
    IonApp,
    IonIcon,
    IonMenu,
    IonRouterOutlet,
    IonSplitPane,
    CommonModule,
    menuItemPage,
  ],
  providers: [MenuController, ToastController],
  encapsulation: ViewEncapsulation.None,
  standalone: true
})
export class AppComponent extends AppPagesBase implements OnInit {
  private router = inject(Router);
  private storage = inject(Storage);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  isDesktopView = false;
  isMenuVisible = true;
  menuIcon = chevronBackOutline;

  appPages = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: 'grid',
    },
    {
      title: 'Projects',
      url: '/projects',
      icon: 'folder',
    },
    {
      title: 'Devices',
      url: '/devices',
      icon: 'phone-portrait',
    },
  ];
  loggedIn = false;
  dark = false;

  constructor(
    private pushNotificationService: PushNotificationService,
    private jwtService: JwtService,
    alertController: AlertController,
    platform: Platform
  ) {
    super(alertController, platform);

    this.initializeApp();
    this.setupDesktopView();

    addIcons({
      calendarOutline,
      chevronBackOutline,
      chevronForwardOutline,
      folderOutline,
      gridOutline,
      logInOutline,
      logOutOutline,
      menuOutline,
      moonOutline,
      personAddOutline,
      personOutline,
      swapHorizontalOutline,
      informationCircleOutline,
      businessOutline,
      phonePortraitOutline,
      bluetoothOutline,
      flaskOutline,
      cubeOutline,
      repeatOutline,
      notificationsOutline,
      peopleOutline,
      checkmarkCircleOutline,
      linkOutline
    });
  }

  async ngOnInit() {
    const savedLang = window.localStorage.getItem('lang') || 'en';
    this.translate.addLangs(['en', 'pl']);
    this.translate.setDefaultLang('en');
    this.translate.use(savedLang);
    this.translate.setFallbackLang('en');
    await this.storage.create();
    // this.checkTokenandNavigate();
    this.checkLoginStatus();
    this.listenForLoginEvents();
  }

  initializeApp() {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      this.pushNotificationService.setup();
    }

    this.platform.ready().then(() => {
      if (this.platform.is('hybrid')) {
        StatusBar.show();
        StatusBar.setBackgroundColor({ color: '#00C853' });
        SplashScreen.hide();
      }
    });
  }

  setupDesktopView() {
    this.isDesktopView = this.platform.width() >= 992;
    window.addEventListener('resize', () => {
      this.isDesktopView = window.innerWidth >= 992;
    });
  }

  checkLoginStatus() {
    return this.userService.isLoggedIn().then(loggedIn => {
      return this.updateLoggedInStatus(loggedIn);
    });
  }

  updateLoggedInStatus(loggedIn: boolean) {
    setTimeout(() => {
      this.loggedIn = loggedIn;
    }, 300);
  }

  listenForLoginEvents() {
    window.addEventListener('user:login', () => {
      this.updateLoggedInStatus(true);
    });

    window.addEventListener('user:signup', () => {
      this.updateLoggedInStatus(true);
    });

    window.addEventListener('user:logout', () => {
      this.updateLoggedInStatus(false);
    });
  }

  toggleDarkMode() {
    document.documentElement.classList.toggle('ion-palette-dark', this.dark);
    document.documentElement.classList.add('color-transition');
    localStorage.setItem('darkMode', this.dark ? 'true' : 'false');
    setTimeout(() => {
      document.documentElement.classList.remove('color-transition');
    }, 300);
  }

  toggleMenu() {
    this.isMenuVisible = !this.isMenuVisible;
    this.menuIcon = this.isMenuVisible ? chevronBackOutline : chevronForwardOutline;
  }

  checkTokenandNavigate() {
    const token = this.jwtService.getToken();

    if (token) {
      this.updateLoggedInStatus(true);
    }

    const refreshToken = window.localStorage.vivent_jwt_refresh_token;

    if (refreshToken) {
      this.jwtService.refreshToken(refreshToken).subscribe({
        next: newToken => {
          const curusername = this.jwtService.getUsername();
          const expireTime = new Date().getTime() + newToken.expires_in * 1000;

          this.jwtService.saveToken(newToken.access_token, newToken.refresh_token, expireTime, curusername);
          this.updateLoggedInStatus(true);
        },
        error: () => {
          this.handleTokenFailure();
        }
      });
    } else {
      this.handleTokenFailure();
    }
  }

  private handleTokenFailure() {
    this.jwtService.destroyToken();
    this.userService.logout();
    this.updateLoggedInStatus(false);
    this.router.navigateByUrl('/login');
  }

}
